import PropTypes from 'prop-types';
import { useState, cloneElement, isValidElement } from 'react';

import { alpha } from '@mui/material/styles';

import Iconify from '../iconify';
import UpgradePopover from '../upgrade-popover';

// ----------------------------------------------------------------------

export default function LimitButton ({
  children,
  isAtLimit = false,
  limitLabel,
  limitIcon = <Iconify icon="solar:lock-bold" />,
  limitVariant = 'soft',
  limitColor = 'warning',
  popoverTitle,
  popoverDescription,
  popoverBenefits = [],
  popoverIcon = 'solar:lock-bold',
  popoverUpgradeUrl = '/account#billing',
}) {
  const [anchorEl, setAnchorEl] = useState(null);

  if (!isValidElement(children)) {
    console.error('LimitButton: children must be a valid React element (a button component)');
    return null;
  }

  const handleClick = (event) => {
    if (isAtLimit) {
      setAnchorEl(event.currentTarget);
    } else {
      // Call the original onClick handler from the button
      children.props.onClick?.(event);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // If at limit, clone button with locked state styling
  if (isAtLimit) {
    const lockedButton = cloneElement(children, {
      ...children.props,
      variant: limitVariant,
      color: limitColor,
      onClick: handleClick,
      startIcon: limitIcon,
      children: limitLabel || children.props.children,
      sx: {
        ...children.props.sx,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          padding: '2px',
          background: (theme) =>
            `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.8)}, ${alpha(
              theme.palette.error.main,
              0.8
            )})`,
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
        },
      },
    });

    return (
      <>
        {lockedButton}

        {popoverTitle && popoverDescription && (
          <UpgradePopover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            title={popoverTitle}
            description={popoverDescription}
            benefits={popoverBenefits}
            icon={popoverIcon}
            upgradeUrl={popoverUpgradeUrl}
          />
        )}
      </>
    );
  }

  // Normal button when not at limit - return as is
  return children;
}

LimitButton.propTypes = {
  children: PropTypes.element.isRequired,
  isAtLimit: PropTypes.bool,
  limitLabel: PropTypes.node,
  limitIcon: PropTypes.node,
  limitVariant: PropTypes.string,
  limitColor: PropTypes.string,
  popoverTitle: PropTypes.string,
  popoverDescription: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  popoverBenefits: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.node])),
  popoverIcon: PropTypes.string,
  popoverUpgradeUrl: PropTypes.string,
};
